import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '~/components/common/Button';
import { useFirstLoadingDetection } from '~/hooks';
import { StyledPanelDetailContainer } from '~/themes/style';
import { usePaymentInfo } from '~/providers/PaymentProvider';
import { cleanDeep, fromQueryString, toQueryString } from '~/utils/queryString';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { StyledFirstLoadingContainer } from '~/layout/PageLayout';
import AddIcon from '~/assets/images/icons/add-white.svg';
import themes from '~/themes';
import { Pagination } from '~/components/common/Pagination';
import { useAuth } from '~/providers/AuthProvider';
import { DEFAULT_PAGE_SIZE } from '~/constants/common';
import { useDeliveryReminders } from '~/hooks/useDeliveryReminders';
import { useGetDriversPagination } from '../apis';
import DialogInviteDriver from '../Invite/DialogInviteDriver';
import { DriverTable } from './DriverTable';
import { DriverDetail } from '../Detail/DriverDetail';
import AddYourDrivers from './AddYourDrivers';
import { UserExpand, UserRole } from '../../Users/types';
import { AddressFilterForm } from './FilterForm';

type DriverListProps = {};

export const DriverList: React.FC<DriverListProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isStarterPlan, setIsOpenUpSellDialog } = usePaymentInfo();
  const [openInviteDriverDialog, setOpenInviteDriverDialog] = useState(false);

  const { account } = useAuth();
  const { newDriver } = useDeliveryReminders();

  const {
    search,
    sort,
    sort_by,
    id: driverId,
    page: p,
    driver_status,
    team_id,
    customer_support,
    shift_schedule,
  } = fromQueryString(location.search, { arrayFormat: 'none' });

  const page = (p || 1) as string;
  const itemPerPage: number = account?.items_per_page || DEFAULT_PAGE_SIZE;

  const {
    data: rawData,
    isFetching,
    refetch,
  } = useGetDriversPagination({
    sort,
    sort_by,
    search,
    driver_status,
    team_id,
    customer_support,
    shift_schedule,
    roles: UserRole.Driver,
    expand: UserExpand.Team,
    page,
    limit: itemPerPage,
  });

  useEffect(() => {
    if (newDriver) {
      refetch();
    }
  }, [newDriver]);

  const isFirstLoading = useFirstLoadingDetection([isFetching]);
  const drivers = useMemo(() => rawData?.items || [], [rawData]);
  const total_pages = useMemo(() => rawData?.total_pages, [rawData]);

  const onSubmit = useCallback(
    (values: any) => {
      cleanDeep(values, { excludeKeys: ['search'], emptyString: true });
      navigate({
        pathname: location.pathname,
        search: toQueryString({ ...values, page: 1 }),
      });
    },
    [location],
  );

  const onClear = useCallback(() => {
    navigate({
      pathname: location.pathname,
      search: toQueryString({
        page: 1,
      }),
    });
  }, [location]);

  const filterComponent = useMemo(
    () => (
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: themes.spacing(1, 2),
          flexWrap: 'wrap',
          marginBottom: themes.spacing(2.4),
          [themes.breakpoints.down('md')]: {
            marginBottom: themes.spacing(1.5),
          },
        }}
      >
        <AddressFilterForm onSubmit={onSubmit} onClear={onClear} />
        <Button
          noRounder
          buttonType='primary-dark'
          onClick={() => {
            if (isStarterPlan && drivers.length >= 1) {
              return setIsOpenUpSellDialog(true);
            }
            return setOpenInviteDriverDialog(true);
          }}
        >
          <Typography
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.4,
            }}
          >
            <img src={AddIcon} alt='add-icon' />
            Add Driver
          </Typography>
        </Button>
      </Stack>
    ),
    [search, location, isStarterPlan, drivers],
  );

  const renderDriverDrawerDetail = () => (
    <StyledPanelDetailContainer isOpen={!!driverId} fixedWidth={640}>
      {driverId && <DriverDetail />}
    </StyledPanelDetailContainer>
  );

  if (isFirstLoading) {
    return (
      <StyledFirstLoadingContainer>
        <CircularProgress />
      </StyledFirstLoadingContainer>
    );
  }

  const isEmptyState =
    !drivers?.length && !isFetching && !location.search.length;

  const renderEmpty = (
    <AddYourDrivers onAddDriver={() => setOpenInviteDriverDialog(true)} />
  );

  return (
    <Stack flex={1} width='100%' overflow='hidden'>
      {filterComponent}
      <Stack className='customized-scrollbar' flex={1} overflow='auto'>
        <DriverTable
          isLoading={isFetching}
          data={drivers || []}
          isEmptyState={isEmptyState}
          renderEmpty={renderEmpty}
        />
      </Stack>{' '}
      {!isEmptyState && (
        <Stack sx={{ pt: 2, alignItems: 'flex-end' }}>
          <Pagination
            totalPages={total_pages}
            currentPage={parseInt(page, 10)}
          />
        </Stack>
      )}
      <DialogInviteDriver
        open={openInviteDriverDialog}
        onClose={() => setOpenInviteDriverDialog(false)}
      />
      {renderDriverDrawerDetail()}
    </Stack>
  );
};
